/** 餐別。組成一天的四個時段，UI 依此分組並各自小計。 */
export type MealSlot = 'breakfast' | 'lunch' | 'dinner' | 'snack';

/**
 * 一筆紀錄的營養素。全部選填。
 *
 * `null` 表示「未填」，與 0 語意不同 —— 前者是不知道、後者是確實不含。
 * 加總時必須跳過 null，不能當 0 算，否則「今天吃了多少蛋白質」會被系統性低估。
 */
export interface Nutrients {
  /** 蛋白質（公克） */
  proteinG: number | null;
  /** 脂肪（公克） */
  fatG: number | null;
  /** 碳水化合物（公克） */
  carbsG: number | null;
  /** 膳食纖維（公克） */
  fiberG: number | null;
  /** 鈉（毫克） */
  sodiumMg: number | null;
}

/** 五項皆未填。 */
export const EMPTY_NUTRIENTS: Nutrients = {
  proteinG: null,
  fatG: null,
  carbsG: null,
  fiberG: null,
  sodiumMg: null,
};

/**
 * 份量的單位。
 *
 * `'gram'` 對使用者顯示為「公克/毫升」：兩者在飲食紀錄的尺度上是同一件事（水 1ml≈1g），
 * 分成兩個值只會逼使用者在「這杯豆漿算幾克」上做一個沒有意義的選擇，也讓既有紀錄需要遷移。
 * 液體到底該讀成毫升，交給品項名稱表達 —— 送去估算的提示詞也是這樣寫的。
 */
export type MeasurementUnit = 'serving' | 'gram';

/**
 * 一筆紀錄的份量。
 *
 * `referencePortionG` 是「**一個單位**等於幾公克」，不是總重 —— 兩份 280g 的義大利麵，
 * 這裡存的是 280 而非 560。存單位量才能在份與公克之間換算，也才在份數改變時仍然有效。
 * 只有經過 AI 估算的紀錄才知道這個值；`null` 表示未知，切換單位時無法換算數值。
 */
export interface Portion {
  quantity: number;
  unit: MeasurementUnit;
  referencePortionG: number | null;
}

/** 未指定份量時的預設：一份，無換算基準。 */
export const DEFAULT_PORTION: Portion = {
  quantity: 1,
  unit: 'serving',
  referencePortionG: null,
};

/**
 * 一筆飲食紀錄。
 *
 * 只有日期沒有時間：一筆紀錄的時間座標是「哪一天的哪一餐」，餐別已經表達了一天
 * 之內的順序。日期存 'YYYY-MM-DD' 而非 timestamp，因為所有查詢都以「某一天」為
 * 單位，字串可直接比對，不必在每次篩選時解析時區。
 *
 * 同一天同一餐的多筆之間，順序即 `entries` 陣列的順序（後端依建立時間排序）。
 */
export interface DietEntry {
  id: string;
  /** 本地時區的 'YYYY-MM-DD' */
  date: string;
  /** 品項名稱 */
  name: string;
  /** 熱量（kcal，非負整數） */
  calories: number;
  /** 餐別。新增時依當下時刻預選，使用者可覆寫 */
  slot: MealSlot;
  /** 營養素，全部選填 */
  nutrients: Nutrients;
  portion: Portion;
}

/** 新增紀錄時的輸入；id 由 store 產生 */
export type DietEntryDraft = Omit<DietEntry, 'id'>;

/**
 * 使用者的飲食設定。每人一份，與紀錄分開存取。
 *
 * 目前只有一個項目，但型別本身即為「設定」這個集合的容器——之後新增設定項是在此加欄位，
 * repository、store 與設定面板都不必再改結構。
 */
export interface DietSettings {
  /** 每日目標熱量（kcal）；null 表示未設定，畫面即不顯示達成度 */
  dailyCalorieGoal: number | null;
}

/** 單日衍生統計。由 entries 即時計算，不存進 store。 */
export interface DailySummary {
  total: number;
  bySlot: Record<MealSlot, number>;
  count: number;
  /** 三大營養素的當日總量（公克）。只加總有填的紀錄 */
  macros: { proteinG: number; fatG: number; carbsG: number };
  /**
   * 三項巨量營養素**都沒填**的筆數。
   *
   * 大於 0 時 `macros` 是不完整的，UI 必須說出來 —— 五筆裡有三筆沒資料卻顯示
   * 「蛋白 82g」，使用者會據此決定晚餐，那比不顯示更糟。
   */
  missingMacroCount: number;
}

/** AI 估算的結果。與 DietEntry 分開：它還沒成為一筆紀錄。 */
export interface NutritionEstimate extends Nutrients {
  foodName: string;
  /** 這次估算的**總**份量（公克）。除以請求的 quantity 才是一個單位的重量 */
  portionGrams: number;
  caloriesKcal: number;
  confidence: 'high' | 'medium' | 'low';
  /** 份量假設與變因說明。**模型產生的自由文字，一律當純文字渲染** */
  notes: string;
  /**
   * 這次實際查證的來源網域（如 `citycafe.com.tw`），沒有查證時為 null。
   *
   * **不顯示在畫面上。** 後端要求模型填寫此欄，以迫使它給出具體查證來源、
   * 不在 notes 寫含糊的依據；作用在提示詞端，UI 不需配合。
   */
  source: string | null;
}

/**
 * 今日的 AI 估算額度。
 *
 * 估算每次都要花錢（會呼叫模型並上網查證），所以有每日上限。剩餘次數要讓使用者
 * 看得到 —— 按到被拒絕才知道用完了，是最差的呈現方式。
 */
export interface EstimateQuota {
  limit: number;
  used: number;
  /** 不會是負數 */
  remaining: number;
}

/**
 * 最近輸入過的品項：從歷史紀錄取出，供表單一鍵帶入。
 *
 * 帶的是**上次那一筆的完整內容**（份量、熱量、營養素），不只是名稱與熱量 ——
 * 只帶熱量的話，上次記的若是「200 公克」，帶回來卻顯示「1 份」，那個熱量就掛在
 * 錯誤的份量標籤下，使用者無從發現。
 *
 * 不含日期與餐別：那兩個由當下的情境決定，不該沿用上次的。
 */
export interface RecentItem {
  name: string;
  /** 最近一次記錄的熱量 */
  calories: number;
  nutrients: Nutrients;
  portion: Portion;
}

/**
 * 一次估算的完整回應：結果加上扣除後的額度。
 */
export interface EstimateResult {
  estimate: NutritionEstimate;
  quota: EstimateQuota;
}
